import { createContext, useEffect, useRef, useState } from "react";
import { toast, Zoom, cssTransition } from "react-toastify";
import { Socket } from "socket.io-client";
import { getLocalUserId } from "../../../components/functions/utilities";
import { useSocket } from "../../../components/Hooks/useSocket";
import {
  DataPeriodType,
  DrawerStateInf,
  IPWorkspace,
  PinnedVisualIdentifiers,
  PinnedVisualIdentifiersState,
  ProgressStage,
  ReportDetailsData,
  WorkspaceCreationWith,
  WorskpaceTitles,
} from "../../../interfaces/interfaces";
import { channelState } from "../ReportDetails";
import { AiOutlineClose, AiOutlineClose as CloseIcon } from "react-icons/ai";
import useModal from "../../../components/Hooks/useModal";
import { DataPieces } from "../Components/ReportTabs/TabsContent/ReportTabsContent";

type ReportDetailsContextType = {
  changeSelectedCardsByIdentifier: (data: number[]) => void;
  progress: number;
  setProgress: (data: number) => void;
  dataPieces: DataPieces;
  setDataPieces: (data: DataPieces) => void;
  progressStage: ProgressStage;
  setProgressStage: (data: ProgressStage) => void;
  setPinnedVisualIdentifiers: (data: PinnedVisualIdentifiersState) => void;
  pinnedVisualIdentifiers: PinnedVisualIdentifiersState;
  reportDetails: ReportDetailsData;
  isChannel: channelState;
  workspace: IPWorkspace;
  setWorkspace: (IPWorkspace) => void;
  setReportDetails: (ReportDetailsData) => void;
  setIsChannel: (channelState) => void;
  socket: Socket;
  handleJoinRoom: (roomIds: string[]) => void;
  versions: Versions[];
  setVersions: (versions: any) => void;
  versionIndex: number;
  setVersionIndex: (versionIndex: number) => void;
  selectedCharts: Set<number>;
  selectedPivotTables: Set<number>;
  selectedCards: Set<number>;
  selectedCohorts: Set<number>;
  changeSelectedCharts: (
    chartId: number,
    onDashboard: boolean,
    pinOnDashboard: boolean,
  ) => void;
  changeSelectedPivotTables: (
    pivotTableId: number,
    onDashboard: boolean,
    pinOnDashboard: boolean,
  ) => void;
  changeSelectedCohorts: (
    cohortId: number,
    onDashboard: boolean,
    pinOnDashboard: boolean,
  ) => void;
  changeSelectedCards: (cardId: number, pinOnDashboard: boolean) => void;
  resetBatchOptions: () => void;
  selectChartsOnDashboard: { [x: number]: boolean };
  selectPivotTablesOnDashboard: { [x: number]: boolean };
  selectPinnedChartsOnDashboard: { [x: number]: boolean };
  selectPinnedPivotTablesOnDashboard: { [x: number]: boolean };
  selectedPinnedCardsOnDashboard: { [x: number]: boolean };
  selectCohortsOnDashboard: { [x: number]: boolean };
  selectedPinnedCohortsOnDashboard: { [x: number]: boolean };
  resetDataPiece: boolean;
  setResetDataPiece: (value: boolean) => void;
  selectAll: boolean;
  changeSelectAll: (value: boolean) => void;
  handleSelectAll: (
    chartId: number[],
    pivotIds: number[],
    cardIds: number[],
    cohortIds: number[],
    chartDashboardObj: DashboardObj,
    pivotsDashboardObj: DashboardObj,
    cardsDashboardObj: DashboardObj,
    cohortsDashboardObj: DashboardObj,
  ) => void;
  downloadAll: string;
  setDownloadAll: (value: string) => void;
  downloadAllLoading: boolean;
  setDownloadAllLoading: (value: boolean) => void;
  downloadAllCallBack: any;
  setDownloadAllCallBack: (value) => void;
  persistDpClickOptions: boolean;
  setPersistDpClickOptions: (value: boolean) => void;
  handleSetToggleMenuFunc: (
    value: (value: boolean) => void,
    createNewChat: () => void,
    chatToggle: (value: boolean) => void,
  ) => void;
  toggleMenuRef: any;
  createNewChatRef: any;
  chatToggleRef: any;
  isTemporaryWorkspace: boolean;
  setIsTemporaryWorkspace: any;
  customDPJobId: number;
  setCustomeDPJobId: any;
  combinedDisconnectedIp: string[];
  setCombinedDisconnectedIp: (value: string[]) => void;
  combinedInsufficientDataSource: InsufficientDataSourceType;
  setCombinedInsufficientDataSource: (
    value: InsufficientDataSourceType,
  ) => void;
  hideDropdownOnScroll: boolean;
  setHideDropdownOnScroll: (value: boolean) => void;
  connectionToggle: any;
  connectionModal: boolean;
  versionLoading: boolean;
  setVersionLoading: (value: boolean) => void;
  currentReportVersion: number;
  setCurrentReportVersion: (value: number) => void;
  ipLogos: {
    [x: string]: string;
  };
  setIpLogos: (value: any) => void;
  isConsolidatedWorkspace: boolean;
  setIsConsolidatedWorkspace: (value: boolean) => void;
  setDrawerState: (value: DrawerStateInf) => void;
  drawerState: DrawerStateInf;
};



type DashboardValueObj = {
  [x: number]: boolean;
};

type DashboardObj = {
  onDashboardObj: DashboardValueObj;
  pinDashboardObj: DashboardValueObj;
};

type InsufficientDataSourceType = {
  inSufficientDs: string[];
  sufficientDs: string[];
  isInsufficient: boolean;
};

export const initialChannelState: channelState = {
  isShow: false,
  channelId: null,
  chartOrPivotId: null,
  target: null,
  hasNoAccess: [],
  reportDate: null,
  chartTarget: null,
  popoverClassname: null,
  isRight: null,
};

export type Versions = {
  id: number;
  createdBy: number;
  parentVersion: number;
  reportId: number;
  templateExcelUserFileId: string;
  updateTime: number;
  versionDataPeriod?: DataPeriodType;
  dataSource?: string;
  isConsolidated?: number;
  accessTokenIds?: any;
  dataPeriodArr?: string[];
};

let initialSet = new Set<number>();

export const ReportDetailstContext =
  createContext<ReportDetailsContextType>(null);

const CloseButton = ({ closeToast }) => (
  <AiOutlineClose className="tick_icon" onClick={closeToast} />
);

const ReportDetailsProvider = ({ children }) => {
  const toastId = useRef(null);
  const userId = getLocalUserId();
  const [pinnedVisualIdentifiers, setPinnedVisualIdentifiers] =
    useState<PinnedVisualIdentifiersState>();
  const [reportDetails, setReportDetails] = useState<ReportDetailsData>();
  const [workspace, setWorkspace] = useState<IPWorkspace>();
  const [isChannel, setIsChannel] = useState<channelState>(initialChannelState);
  const { socket, handleJoinRoom } = useSocket(userId.toString());
  const [versions, setVersions] = useState<Versions[]>([]);
  const [versionIndex, setVersionIndex] = useState(null);
  const [selectedCharts, setSelectedCharts] = useState<Set<number>>(initialSet);
  const [selectedCards, setSelectedCards] = useState<Set<number>>(initialSet);
  const [selectedCohorts, setSelectedCohorts] =
    useState<Set<number>>(initialSet);
  const [selectedPivotTables, setSelectedPivotTables] =
    useState<Set<number>>(initialSet);
  const [selectChartsOnDashboard, setSelectChartsOnDashboard] = useState({});
  const [selectPivotTablesOnDashboard, setSelectPivotTablesOnDashboard] =
    useState({});
  const [selectCohortsOnDashboard, setSelectCohortsOnDashboard] = useState({});
  const [selectPinnedChartsOnDashboard, setSelectPinnedChartsOnDashboard] =
    useState({});
  const [
    selectPinnedPivotTablesOnDashboard,
    setSelectPinnedPivotTablesOnDashboard,
  ] = useState({});
  const [
    selectedPinnedCohortsOnDashboard,
    setSelectedPinnedCohortsOnDashboard,
  ] = useState({});
  const [selectedPinnedCardsOnDashboard, setSelectedPinnedCardsOnDashboard] =
    useState({});

  const [resetDataPiece, setResetDataPiece] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [downloadAll, setDownloadAll] = useState("");
  const [downloadAllLoading, setDownloadAllLoading] = useState(false);
  const [downloadAllCallBack, setDownloadAllCallBack] = useState();
  const [persistDpClickOptions, setPersistDpClickOptions] = useState(false);
  const toggleMenuRef = useRef(null);
  const createNewChatRef = useRef(null);
  const chatToggleRef = useRef(null);
  const { toggle: connectionToggle, visible: connectionModal } =
    useModal(false);

  const [isTemporaryWorkspace, setIsTemporaryWorkspace] = useState(false);
  const [customDPJobId, setCustomeDPJobId] = useState(0);
  const [combinedDisconnectedIp, setCombinedDisconnectedIp] = useState([]);
  const [combinedInsufficientDataSource, setCombinedInsufficientDataSource] =
    useState<InsufficientDataSourceType>({
      sufficientDs: [],
      inSufficientDs: [],
      isInsufficient: false,
    });
  const [hideDropdownOnScroll, setHideDropdownOnScroll] = useState(false);
  const [versionLoading, setVersionLoading] = useState(false);
  const [currentReportVersion, setCurrentReportVersion] = useState(null);
  const [progress, setProgress] = useState(0);
  const [progressStage, setProgressStage] = useState(ProgressStage.stage_0);

  const [dataPieces, setDataPieces] = useState<DataPieces>({
    charts: [],
    pivot: [],
    cards: [],
    cohorts: [],
  });

  const [ipLogos, setIpLogos] = useState({});
  const [isConsolidatedWorkspace, setIsConsolidatedWorkspace] = useState(false);
  const [drawerState, setDrawerState] = useState<DrawerStateInf>({
    isOpen: false,
    cardDetails: null
  })

  // useEffect(() => {
  //   if (reportDetails?.report?.initialReportName == WorskpaceTitles.consolidatedWorkspace) {
  //     setIsConsolidatedWs(true);
  //   } else {
  //     setIsConsolidatedWs(false);
  //   }
  // }, [reportDetails])

  const handleSetToggleMenuFunc = (
    toggleMenu: (value: boolean) => void,
    createNewChat: () => void,
    chatToggle: (value: boolean) => void,
  ) => {
    toggleMenuRef.current = toggleMenu;
    createNewChatRef.current = createNewChat;
    chatToggleRef.current = chatToggle;
  };

  const changeSelectedCardsByIdentifier = (cardIdentifiers: any[]) => {
    if (!reportDetails?.report?.id) return;

    // Empty the data piece selection
    setSelectedCards(new Set());
    setSelectedPinnedCardsOnDashboard({});

    // Select the cards by identifier
    const selectedCardIds = new Set<number>();
    const selectedCardIdsOnDashboard = {};
    dataPieces?.cards?.forEach((card) => {
      if (cardIdentifiers.includes(card.cardIdentifier)) {
        selectedCardIds.add(card.id);
        selectedCardIdsOnDashboard[card.id] = pinnedVisualIdentifiers.cards.has(
          `${reportDetails.report.id}-${card.cardIdentifier}`,
        );
      }
    });

    setSelectedCards(selectedCardIds);
    setSelectedPinnedCardsOnDashboard(selectedCardIdsOnDashboard);
  };

  const changeSelectedCards = (cardId: number, pinToDashboard: boolean) => {
    let oldSet = new Set(Array.from(selectedCards));
    let newPinnedSelectedCardsOnDashboard = selectedPinnedCardsOnDashboard;
    if (oldSet.has(cardId)) {
      oldSet.delete(cardId);
      delete newPinnedSelectedCardsOnDashboard[cardId];
      setSelectedPinnedCardsOnDashboard(newPinnedSelectedCardsOnDashboard);
    } else {
      oldSet.add(cardId);
      newPinnedSelectedCardsOnDashboard = {
        ...newPinnedSelectedCardsOnDashboard,
        [cardId]: pinToDashboard,
      };
      setSelectedPinnedCardsOnDashboard(newPinnedSelectedCardsOnDashboard);
      notify(
        `${oldSet.size} ${oldSet.size > 1 ? "datapieces" : "datapiece"
        } selected`,
      );
    }
    setSelectedCards(oldSet);
  };

  const changeSelectedCharts = (
    chartId: number,
    onDashboard: boolean,
    pinToDashboard: boolean,
  ) => {
    const oldSet = new Set(Array.from(selectedCharts));
    let newSelectedChartsOnDashboard = selectChartsOnDashboard;
    let newSelectPinnedChartsDashboard = selectPinnedChartsOnDashboard;
    toast.dismiss(toastId.current);
    if (oldSet.has(chartId)) {
      oldSet.delete(chartId);
      delete newSelectedChartsOnDashboard[chartId];
      delete newSelectPinnedChartsDashboard[chartId];
      setSelectPinnedChartsOnDashboard(newSelectPinnedChartsDashboard);
      setSelectChartsOnDashboard(newSelectedChartsOnDashboard);

      // notify("1 datapiece deselected");
      // toast.info("1 datapiece deselected", {
      //     position: toast.POSITION.BOTTOM_LEFT,
      //     hideProgressBar: true,
      //     autoClose: 2000,
      // });
    } else {
      oldSet.add(chartId);
      newSelectedChartsOnDashboard = {
        ...newSelectedChartsOnDashboard,
        [chartId]: onDashboard,
      };
      newSelectPinnedChartsDashboard = {
        ...newSelectPinnedChartsDashboard,
        [chartId]: pinToDashboard,
      };
      setSelectChartsOnDashboard(newSelectedChartsOnDashboard);
      setSelectPinnedChartsOnDashboard(newSelectPinnedChartsDashboard);
      // toast.info("1 datapiece selected", {
      //     position: toast.POSITION.BOTTOM_LEFT,
      //     hideProgressBar: true,
      //     autoClose: 2000,
      // });
      notify(
        `${oldSet.size} ${oldSet.size > 1 ? "datapieces" : "datapiece"
        } selected`,
      );
    }
    setSelectedCharts(oldSet);
  };

  const notify = (message) =>
  (toastId.current = toast(message, {
    position: toast.POSITION.BOTTOM_LEFT,
    hideProgressBar: true,
    autoClose: 2000,
    className: "dp_toast_message tick_icon",
    bodyClassName: "dp_toast_body_message tick_icon",
    closeButton: CloseButton,
    transition: Zoom,
  }));

  const changeSelectedCohorts = (
    cohortId: number,
    onDashboard: boolean,
    pinToDashboard: boolean,
  ) => {
    const oldSet = new Set(Array.from(selectedCohorts));
    let newSelectedCohortsOnDashboard = selectCohortsOnDashboard;
    let newSelectPinnedCohortsDashboard = selectedPinnedCohortsOnDashboard;
    if (oldSet.has(cohortId)) {
      oldSet.delete(cohortId);
      delete newSelectedCohortsOnDashboard[cohortId];
      delete newSelectPinnedCohortsDashboard[cohortId];
      setSelectPivotTablesOnDashboard(newSelectedCohortsOnDashboard);
      setSelectPinnedChartsOnDashboard(newSelectPinnedCohortsDashboard);
    } else {
      oldSet.add(cohortId);
      newSelectedCohortsOnDashboard = {
        ...newSelectedCohortsOnDashboard,
        [cohortId]: onDashboard,
      };
      newSelectPinnedCohortsDashboard = {
        ...newSelectPinnedCohortsDashboard,
        [cohortId]: pinToDashboard,
      };
      setSelectCohortsOnDashboard(newSelectedCohortsOnDashboard);
      setSelectedPinnedCohortsOnDashboard(newSelectPinnedCohortsDashboard);
    }
    setSelectedCohorts(oldSet);
  };

  const changeSelectedPivotTables = (
    pivotTableId: number,
    onDashboard: boolean,
    pinToDashboard: boolean,
  ) => {
    const oldSet = new Set(Array.from(selectedPivotTables));
    let newSelectedPivotsOnDashboard = selectPivotTablesOnDashboard;
    let newSelectPinnedPivotTablesDashboard =
      selectPinnedPivotTablesOnDashboard;
    if (oldSet.has(pivotTableId)) {
      oldSet.delete(pivotTableId);
      delete newSelectedPivotsOnDashboard[pivotTableId];
      delete newSelectPinnedPivotTablesDashboard[pivotTableId];
      setSelectPivotTablesOnDashboard(newSelectedPivotsOnDashboard);
      setSelectPinnedChartsOnDashboard(newSelectPinnedPivotTablesDashboard);
    } else {
      oldSet.add(pivotTableId);
      newSelectedPivotsOnDashboard = {
        ...newSelectedPivotsOnDashboard,
        [pivotTableId]: onDashboard,
      };
      newSelectPinnedPivotTablesDashboard = {
        ...newSelectPinnedPivotTablesDashboard,
        [pivotTableId]: pinToDashboard,
      };
      setSelectPivotTablesOnDashboard(newSelectedPivotsOnDashboard);
      setSelectPinnedPivotTablesOnDashboard(
        newSelectPinnedPivotTablesDashboard,
      );
    }
    setSelectedPivotTables(oldSet);
  };

  const resetBatchOptions = () => {
    setSelectAll(false);
    setSelectedCharts(initialSet);
    setSelectedPivotTables(initialSet);
    setSelectedCards(initialSet);
    setSelectedCohorts(initialSet);
    // setSelectChartsOnDashboard({});
    // setSelectPivotTablesOnDashboard({});
    if (Object.keys(selectPinnedChartsOnDashboard).length)
      setSelectPinnedChartsOnDashboard({});
    if (Object.keys(selectPinnedPivotTablesOnDashboard).length)
      setSelectPinnedPivotTablesOnDashboard({});
    if (Object.keys(selectedPinnedCardsOnDashboard).length)
      setSelectedPinnedCardsOnDashboard({});
    if (Object.keys(selectedPinnedCohortsOnDashboard).length)
      setSelectedPinnedCohortsOnDashboard({});
  };

  const changeSelectAll = (value: boolean) => {
    if (value) {
      setSelectAll(true);
      // notify(`Selected all datapieces`);
    } else {
      // notify(`Deselected all datapieces`);
      resetBatchOptions();
    }
  };

  const handleSelectAll = (
    chartIds: number[],
    pivotIds: number[],
    cardIds: number[],
    cohortIds: number[],
    chartDashboardObj: DashboardObj,
    pivotsDashboardObj: DashboardObj,
    cardDashboardObj: DashboardObj,
    cohortDashboardObj: DashboardObj,
  ) => {
    notify(
      `${cohortIds.length + cardIds.length + chartIds.length + pivotIds.length
      } datapieces selected`,
    );
    setSelectedCharts(new Set(chartIds));
    setSelectedPivotTables(new Set(pivotIds));
    setSelectedCards(new Set(cardIds));
    setSelectedCohorts(new Set(cohortIds));

    setSelectPinnedChartsOnDashboard(chartDashboardObj.pinDashboardObj);
    setSelectPinnedPivotTablesOnDashboard(pivotsDashboardObj.pinDashboardObj);
    setSelectedPinnedCardsOnDashboard(cardDashboardObj.pinDashboardObj);
    setSelectedPinnedCohortsOnDashboard(cohortDashboardObj.pinDashboardObj);
  };

  return (
    <ReportDetailstContext.Provider
      value={{
        progress,
        setProgress,
        progressStage,
        setProgressStage,
        pinnedVisualIdentifiers,
        setPinnedVisualIdentifiers,
        reportDetails,
        workspace,
        setWorkspace,
        isChannel,
        setReportDetails,
        setIsChannel,
        socket,
        handleJoinRoom,
        versions,
        setVersions,
        versionIndex,
        setVersionIndex,
        selectedCharts,
        selectedCards,
        selectedPivotTables,
        selectedCohorts,
        changeSelectedCharts,
        changeSelectedPivotTables,
        changeSelectedCards,
        changeSelectedCohorts,
        resetBatchOptions,
        selectChartsOnDashboard,
        selectPivotTablesOnDashboard,
        selectCohortsOnDashboard,
        selectPinnedChartsOnDashboard,
        selectPinnedPivotTablesOnDashboard,
        selectedPinnedCardsOnDashboard,
        selectedPinnedCohortsOnDashboard,
        resetDataPiece,
        setResetDataPiece,
        selectAll,
        handleSelectAll,
        changeSelectAll,
        downloadAll,
        setDownloadAll,
        downloadAllLoading,
        setDownloadAllLoading,
        downloadAllCallBack,
        setDownloadAllCallBack,
        persistDpClickOptions,
        setPersistDpClickOptions,
        toggleMenuRef,
        handleSetToggleMenuFunc,
        chatToggleRef,
        createNewChatRef,
        isTemporaryWorkspace,
        setIsTemporaryWorkspace,
        customDPJobId,
        setCustomeDPJobId,
        combinedDisconnectedIp,
        setCombinedDisconnectedIp,
        combinedInsufficientDataSource,
        setCombinedInsufficientDataSource,
        hideDropdownOnScroll,
        setHideDropdownOnScroll,
        connectionToggle,
        connectionModal,
        versionLoading,
        setVersionLoading,
        currentReportVersion,
        setCurrentReportVersion,
        dataPieces,
        setDataPieces,
        changeSelectedCardsByIdentifier,
        ipLogos,
        setIpLogos,
        isConsolidatedWorkspace,
        setIsConsolidatedWorkspace,
        drawerState,
        setDrawerState
      }}
    >
      {children}
    </ReportDetailstContext.Provider>
  );
};

export default ReportDetailsProvider;
